import React, { useState } from 'react';
import { Amplify } from 'aws-amplify';
import awsExports from './aws-exports';
import { withAuthenticator, ThemeProvider } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import axios from 'axios';

import { message, Upload, Image } from 'antd';
import lBBackground from './images/header_RAPLLockBox.png';
import HeaderImage from './images/Rialto_Logo.png';
import uploaderLogo from './images/UploaderLogo.png';

Amplify.configure(awsExports);

const { Dragger } = Upload;

// --- Styles ---
const instructionStyle = {
  backgroundImage: `url(${lBBackground})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  minHeight: '400px',
  height: 'auto',
  padding: '40px 0',
  width: '80%',           // Changed from 100%
  margin: '0 auto',       // Added to center the banner
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '8px',    // Optional: matches your textStyle rounding
};

const textStyle = {
  fontFamily: 'Arial',
  fontSize: '24px',
  color: 'black',
  width: '90%',           // Changed from 75% for better fit in the narrower box
  textAlign: 'left',
  padding: '20px',
  borderRadius: '8px'
};

const rialtoTheme = {
  name: 'rialto-theme',
  tokens: {
    colors: {
      brand: {
        primary: {
          10: { value: '#f0bc81' },
          20: { value: '#f79420' },
          40: { value: '#f79420' },
          60: { value: '#eea149' },
          80: { value: '#f79420' },
          90: { value: '#cb791c' },
          100: { value: '#8f5716' },
        },
      },
    },
    components: {
      button: {
        primary: {
          backgroundColor: { value: '{colors.brand.primary.60}' },
          _hover: { backgroundColor: { value: '{colors.brand.primary.80}' } },
          _focus: { backgroundColor: { value: '{colors.brand.primary.80}' } },
          _active: { backgroundColor: { value: '{colors.brand.primary.90}' } },
        },
      },
    },
  },
};

// --- Helper for Cognito Token ---
const getIdToken = () => {
  const keys = Object.keys(localStorage);
  const idTokenKey = keys.find(key => key.endsWith('.idToken'));
  return idTokenKey ? localStorage.getItem(idTokenKey) : null;
};

// --- Auth UI Customizations ---
const formFields = {
  signIn: {
    username: { placeholder: 'Enter your email address...', label: 'Email' },
    password: { placeholder: 'Enter your password...', label: 'Password' }
  },
  signUp: {
    email: { order: 1, isRequired: true },
    password: { label: 'New Password', placeholder: 'Min. 8 characters', order: 2 },
    confirm_password: { label: 'Confirm Your Password', order: 3 },
  },
};

const components = {
  Header() {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <img src={require("./images/Rialto_Logo.png")} alt="Logo" style={{ width: '500px' }} />
        <p>Welcome to the Rialto Adjudications LockBox.</p>
        <p>Please Sign in or create an account to upload your files.</p>
      </div>
    );
  },
  Footer() {
    return <p style={{ textAlign: 'center' }}>Need help? Email info@rialtoadjudications.com <br />OR phone 1300 986 440</p>;
  }
};

// --- Main App Component ---
function App({ signOut, user }) {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Parallel Upload Logic
const handleManualUpload = async () => {
    const token = getIdToken();
    if (!token || fileList.length === 0) return;

    setUploading(true);
    const fileMetadata = fileList.map(f => ({ name: f.name, type: f.type }));

    try {
        // STEP 1: Get all URLs from API in one go
        const { data: { uploads } } = await axios.post(
            'https://api.rialtoadjudications.com.au/prepare-upload',
            { files: fileMetadata },
            { headers: { Authorization: `Bearer ${token}` } }
        );

        // STEP 2: Parallel Upload to S3
        const uploadPromises = uploads.map(item => {
            const fileObject = fileList.find(f => f.name === item.fileName);
            return axios.put(item.uploadUrl, fileObject, {
                headers: { 'Content-Type': fileObject.type || 'application/octet-stream' }
            });
        });

        await Promise.all(uploadPromises);
        message.success("Files stored in S3.");

        // STEP 3: Tell API to send the Email
        await axios.post(
            'https://api.rialtoadjudications.com.au/complete-upload',
            { fileNames: fileMetadata.map(f => f.name) },
            { headers: { Authorization: `Bearer ${token}` } }
        );

        // message.success("Notification email sent.",15);
        alert("Files uploaded successfully. You should recieve an email reciept shortly. If you dont receive an email with 15 minutes please contact Rialto Adjudications to confirm files are uploaded")
        setFileList([]);
    } catch (err) {
        console.error(err);
        message.error("Email failed to send..");
        alert("Email failed to send. please contact Rialto Adjudications to confirm files are uploaded.")
    } finally {
        setUploading(false);
    }
};

  const draggerProps = {
    multiple: true,
    fileList,
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      const isLt5G = file.size / 1024 / 1024 / 1024 < 5;
      if (!isLt5G) {
        message.error(`${file.name} is larger than 5GB.`);
        return Upload.LIST_IGNORE;
      }
      // Return false to prevent automatic upload, add to local state instead
      setFileList((prev) => [...prev, file]);
      return false;
    },
  };

  return (
    <ThemeProvider theme={rialtoTheme}>
      {/* Header Section */}
      <div style={{ width: '80%', margin: '20px auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <a href="https://rialtoadjudications.com.au"><Image width={200} alt="Rialto Logo" src={HeaderImage} preview={false} /></a>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
          <button 
            onClick={signOut}
            style={{ backgroundColor: '#ff8c00', color: 'white', border: 'none', padding: '10px 20px', fontSize: '16px', fontWeight: 'bold', borderRadius: '4px', cursor: 'pointer' }}
          >
            SIGN OUT
          </button>
          <h4 style={{ margin: 0, fontWeight: 'normal', fontSize: '14px', color: '#555' }}>
            Welcome: <strong>{user.signInDetails?.loginId}</strong>
          </h4>
        </div>
      </div>

      {/* Instruction Banner */}
      <div style={instructionStyle}>
        <div style={textStyle}>
          <h1>RAPL-LockBox <br /> Secure Document Upload</h1>
          <p>Rialto Adjudications file upload tool RAPL-LockBox is a secure way to deliver your Adjudication Application to us.</p>
          <ul>
            <li>Secure 128 Bit SSL security.</li>
            <li>Unlimited number of files.</li>
            <li>Files up to 5 Gigabytes (5GB) supported.</li>
          </ul>
          <p>We recommend RAPL-LockBox over email for large files to avoid attachment size restrictions.</p>
        </div>
      </div>

      {/* Upload Section */}
      <div style={{ width: '80%', margin: '40px auto' }}>
        <div style={{ marginBottom: '20px' }}>
          <Dragger {...draggerProps}>
            <p className="ant-upload-drag-icon">
              <img src={uploaderLogo} alt="upload-icon" style={{ width: '64px' }} />
            </p>
            <p className="ant-upload-text">Click or drag files to this area to queue</p>
            <p className="ant-upload-hint">Files will only be sent when you click the button below.</p>
          </Dragger>
        </div>

        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleManualUpload}
            disabled={fileList.length === 0 || uploading}
            style={{
              backgroundColor: (fileList.length === 0 || uploading) ? '#d9d9d9' : '#f79420',
              color: 'white',
              border: 'none',
              padding: '15px 50px',
              fontSize: '18px',
              fontWeight: 'bold',
              borderRadius: '4px',
              cursor: (fileList.length === 0 || uploading) ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s'
            }}
          >
            {uploading ? 'UPLOADING...' : `START UPLOAD (${fileList.length} FILES)`}
          </button>
        </div>
      </div>
    </ThemeProvider>
  );
}

const AuthenticatedApp = withAuthenticator(App, { formFields, components });

export default function Root() {
  return (
    <ThemeProvider theme={rialtoTheme}>
      <AuthenticatedApp />
    </ThemeProvider>
  );
}